import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Minus, Receipt, Wallet, Banknote, Coins, Trash2, Pencil, Link as LinkIcon, Image as ImageIcon,
  CheckCircle2, ListChecks, BookOpenCheck, Loader2, Printer, Wrench, AlertTriangle, ChevronLeft, ChevronRight,
  History as HistoryIcon,
} from "lucide-react";
import { Link } from "react-router-dom";
import LedgerFullHistoryDialog from "@/components/LedgerFullHistoryDialog";

// Phase 30 — Settlement PDF download. Auth-aware fetch → blob link.
export async function downloadSettlementReceipt(settlementId) {
  if (!settlementId) return;
  const token = localStorage.getItem("jb_token");
  try {
    const res = await fetch(`${API_BASE}/staff/settlements/${settlementId}/receipt.pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      let detail = `HTTP ${res.status}`;
      try {
        const j = await res.json();
        if (j?.detail) detail = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
      } catch { /* ignore */ }
      throw new Error(detail);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const short = (settlementId || "settlement").split("-")[0];
    a.download = `settlement-${short}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error(`Receipt download failed: ${e?.message || e}`);
  }
}

const TYPE_META = {
  cash_intake:               { sign: -1, label: "Cash intake",            short: "Intake",   icon: Wallet,   color: "text-amber-700"   },
  expense:                   { sign: +1, label: "Expense",                short: "Expense",  icon: Receipt,  color: "text-emerald-700" },
  payout:                    { sign: -1, label: "Payout",                 short: "Payout",   icon: Banknote, color: "text-rose-700"    },
  // Phase P.4 Item 3 — manager-only kinds so bonuses + off-book payouts
  // are distinguishable from regular payouts on the ledger + reports.
  bonus:                     { sign: -1, label: "Bonus",                  short: "Bonus",    icon: Banknote, color: "text-violet-700"  },
  off_book_payout:           { sign: -1, label: "Off-book payout",        short: "Off-book", icon: Banknote, color: "text-fuchsia-700" },
  cash_collected_from_staff: { sign: +1, label: "Cash collected (return)", short: "Returned", icon: Coins,    color: "text-sky-700"     },
};

const fmtMoney = (n, ccy = "EUR") => {
  const num = Number(n || 0);
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: ccy, currencyDisplay: "narrowSymbol" }).format(num);
  } catch { return `€${num.toFixed(2)}`; }
};
const netCopy = (net) => {
  if (Math.abs(net) < 0.005) return { tone: "settled", text: "Settled" };
  return net > 0
    ? { tone: "owed-to-staff",   text: `Company owes staff ${fmtMoney(net)}` }
    : { tone: "owed-from-staff", text: `Staff is holding ${fmtMoney(Math.abs(net))} of company cash` };
};

export default function StaffLedger() {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  // Phase 60.1 — reconcile flow.
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [reconcileReport, setReconcileReport] = useState(null);
  const [reconcileBusy, setReconcileBusy] = useState(false);
  const [reconcilePage, setReconcilePage] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/staff-ledger/summary");
      setRows(data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openReconcile = async () => {
    setReconcileBusy(true);
    setReconcileOpen(true);
    setReconcilePage(0);
    try {
      const { data } = await api.post("/admin/reconcile-staff-ledger");
      setReconcileReport(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
      setReconcileOpen(false);
    } finally {
      setReconcileBusy(false);
    }
  };

  const executeReconcile = async () => {
    setReconcileBusy(true);
    try {
      const { data } = await api.post("/admin/reconcile-staff-ledger", null, {
        params: { dry_run: "false" },
      });
      toast.success(`Backfilled ${data.created} ledger ${data.created === 1 ? "entry" : "entries"}`);
      setReconcileOpen(false);
      setReconcileReport(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setReconcileBusy(false);
    }
  };

  if (selected) {
    return <StaffDrillDown staffId={selected.staff_id} staffName={selected.staff_name} onBack={() => { setSelected(null); load(); }} />;
  }

  return (
    <div className="space-y-6" data-testid="staff-ledger-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-stone-500">// Cash ledger</div>
          <h1 className="text-3xl font-black mt-1">Staff cash ledger</h1>
          <p className="text-stone-600 text-sm mt-1 max-w-3xl">
            Track per-staff cash flow: intakes (staff holding company money), expenses (company reimburses), payouts (cash already paid to staff), and returns (staff handing cash back). Auto-entries fire when an invoice is marked paid via <span className="mono">cash to driver</span>.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 border-amber-400 text-amber-900 hover:bg-amber-50"
          onClick={openReconcile}
          data-testid="staff-ledger-reconcile-button"
          title="Find approvals + bookings that should have a ledger entry but don't"
        >
          <Wrench className="h-4 w-4" /> Reconcile
        </Button>
      </div>

      <ReconcileDialog
        open={reconcileOpen}
        onOpenChange={(v) => { setReconcileOpen(v); if (!v) setReconcileReport(null); }}
        report={reconcileReport}
        busy={reconcileBusy}
        page={reconcilePage}
        onPageChange={setReconcilePage}
        onExecute={executeReconcile}
      />

      {loading && rows.length === 0 ? <div className="text-stone-500 text-sm">Loading…</div> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="staff-ledger-summary-grid">
          {rows.map((r) => {
            // Phase J fix — pre-Phase-J, this card read `r.net_amount_owed_to_staff`
            // which is the LIFETIME net (settled + unsettled). After a Settle to
            // Zero, that number stayed stuck at the historical total. The
            // unsettled-only balance is what the manager actually wants to see
            // here — same field the Settle button already reads.
            const displayNet = Number(
              r.outstanding_balance ?? r.net_amount_owed_to_staff ?? 0
            );
            const copy = netCopy(displayNet);
            const toneCls = copy.tone === "settled" ? "border-stone-300 bg-stone-50"
                          : copy.tone === "owed-to-staff" ? "border-emerald-300 bg-emerald-50/60"
                          : "border-rose-300 bg-rose-50/60";
            return (
              <button key={r.staff_id} onClick={() => setSelected(r)}
                      className={`text-left rounded-[8px] border-2 ${toneCls} p-4 hover:shadow-md transition-shadow`}
                      data-testid={`ledger-summary-card-${r.staff_id}`}>
                <div className="flex items-center justify-between">
                  <div className="font-bold text-lg">{r.staff_name}</div>
                  {!r.active && <span className="text-[10px] mono uppercase bg-stone-200 text-stone-600 px-1.5 py-0.5 rounded">inactive</span>}
                </div>
                <div className="mono text-[10px] uppercase tracking-wider text-stone-500 mt-1">Net position</div>
                <div className={`text-2xl font-black ${copy.tone === "owed-to-staff" ? "text-emerald-800" : copy.tone === "owed-from-staff" ? "text-rose-800" : "text-stone-700"}`}
                     data-testid={`ledger-net-value-${r.staff_id}`}>
                  {copy.tone === "settled" ? "—" : fmtMoney(Math.abs(displayNet))}
                </div>
                <div className="text-xs text-stone-600 mt-0.5">{copy.text}</div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-3 text-xs">
                  {Object.entries(TYPE_META).map(([k, m]) => (
                    <div key={k} className="flex items-center justify-between">
                      <span className="text-stone-500">{m.short}</span>
                      <span className={`mono font-bold ${(r.subtotals?.[k] || 0) > 0 ? "" : "text-stone-400"}`}>
                        {fmtMoney(r.subtotals?.[k] || 0)}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="mt-2 text-[10px] mono uppercase tracking-wider text-stone-400">{r.entry_count} entries · click to drill in</div>
              </button>
            );
          })}
          {rows.length === 0 && <div className="text-stone-500 text-sm col-span-full">No staff yet — add some on the Staff page.</div>}
        </div>
      )}
    </div>
  );
}

function StaffDrillDown({ staffId, staffName, onBack }) {
  // Phase P.37 — also exported below as `StaffLedgerDetail` so the
  // unified /ledger page can mount this drilldown directly for a chosen
  // staff wallet without going through the list view.
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState({ entry_type: "", from: "", to: "" });
  // Phase P.20 — Log payout button/dialog removed. New +/- flow handles all
  // manager-side ledger entries via cash_type on-book / off-book toggle.
  const [creating, setCreating] = useState(null); // null | "expense" | "cash_intake"
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("entries"); // "entries" | "statement"
  const [serverSummary, setServerSummary] = useState(null);
  const [settling, setSettling] = useState(false);
  // Phase P.30 — full audit-history modal.
  const [historyOpen, setHistoryOpen] = useState(false);
  // Phase P.20 — removed `payoutOpen` (Log payout dialog killed).

  const load = async () => {
    try {
      const params = {};
      if (filter.entry_type) params.entry_type = filter.entry_type;
      if (filter.from) params.from = filter.from;
      if (filter.to) params.to = filter.to;
      const [{ data }, { data: summ }] = await Promise.all([
        api.get(`/staff/${staffId}/ledger`, { params }),
        api.get(`/staff/${staffId}/ledger/summary`),
      ]);
      setEntries(data);
      setServerSummary(summ);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [staffId, filter]);

  // Phase J fix — Settle to Zero left the displayed "Net position" stuck
  // on the historical total because this client-side sum walked ALL entries
  // (settled + unsettled). After settle, every entry gets `settlement_id`
  // stamped server-side, so filtering to `!e.settlement_id` drops the net
  // straight to zero immediately, with no manual refresh needed.
  const summary = useMemo(() => {
    const subtotals = { cash_intake: 0, expense: 0, payout: 0, cash_collected_from_staff: 0 };
    const unsettled = entries.filter((e) => !e.settlement_id);
    unsettled.forEach((e) => { subtotals[e.entry_type] = (subtotals[e.entry_type] || 0) + Number(e.amount || 0); });
    const net = subtotals.expense + subtotals.cash_collected_from_staff - subtotals.cash_intake - subtotals.payout;
    return { subtotals, net };
  }, [entries]);
  const copy = netCopy(summary.net);

  const remove = async (entry) => {
    if (entry.auto_generated && !confirm("This is an AUTO entry from a paid invoice. Deleting it here breaks the link with the invoice. Continue?")) return;
    if (!entry.auto_generated && !confirm("Delete this ledger entry?")) return;
    try {
      await api.delete(`/staff-ledger/${entry.id}`);
      toast.success("Entry deleted");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="staff-ledger-drilldown">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <button onClick={onBack} className="text-stone-500 hover:text-stone-800 text-sm inline-flex items-center gap-1"><ArrowLeft className="h-4 w-4" /> All staff</button>
          <h1 className="text-3xl font-black mt-1">{staffName} · ledger</h1>
        </div>
        <div className="flex items-center gap-2">
          {serverSummary && Math.abs(serverSummary.outstanding_balance || 0) >= 0.005 && (
            <Button
              onClick={() => setSettling(true)}
              className="bg-emerald-700 hover:bg-emerald-600 text-white"
              data-testid="ledger-settle-to-zero-btn"
            >
              <CheckCircle2 className="h-4 w-4 mr-1" />
              Settle to Zero ({fmtMoney(Math.abs(serverSummary.outstanding_balance || 0))})
            </Button>
          )}
          <Button
            onClick={() => setCreating("expense")}
            variant="outline"
            className="gap-2 border-rose-300 text-rose-700 hover:bg-rose-50"
            data-testid="ledger-add-expense-btn"
          >
            <Minus className="h-4 w-4" /> Add expense
          </Button>
          <Button
            onClick={() => setCreating("cash_intake")}
            className="btn-cta gap-2"
            data-testid="ledger-add-intake-btn"
          >
            <Plus className="h-4 w-4" /> Add intake
          </Button>
          <Button
            onClick={() => setHistoryOpen(true)}
            variant="outline"
            className="gap-2"
            data-testid="ledger-full-history-btn"
          >
            <HistoryIcon className="h-4 w-4" /> Full history
          </Button>
        </div>
      </div>

      {/* Phase 28 — Tab switcher: Entries vs Statement */}
      <div className="inline-flex rounded-full border border-stone-300 bg-white p-1 gap-1" role="tablist" data-testid="ledger-tab-switcher">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "entries"}
          onClick={() => setTab("entries")}
          className={`px-4 py-1.5 text-sm rounded-full transition-colors inline-flex items-center gap-1.5 ${tab === "entries" ? "bg-stone-900 text-white font-bold" : "text-stone-500 hover:text-stone-800"}`}
          data-testid="ledger-tab-entries"
        >
          <ListChecks className="h-3.5 w-3.5" /> Entries
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "statement"}
          onClick={() => setTab("statement")}
          className={`px-4 py-1.5 text-sm rounded-full transition-colors inline-flex items-center gap-1.5 ${tab === "statement" ? "bg-stone-900 text-white font-bold" : "text-stone-500 hover:text-stone-800"}`}
          data-testid="ledger-tab-statement"
        >
          <BookOpenCheck className="h-3.5 w-3.5" /> Statement
        </button>
      </div>

      {/* Net summary band */}
      <div className={`rounded-[8px] border-2 p-4 flex items-center justify-between flex-wrap gap-3
        ${copy.tone === "settled" ? "border-stone-300 bg-stone-50" : copy.tone === "owed-to-staff" ? "border-emerald-300 bg-emerald-50" : "border-rose-300 bg-rose-50"}`}>
        <div>
          <div className="mono text-[10px] uppercase tracking-wider text-stone-500">Net position</div>
          <div className={`text-3xl font-black ${copy.tone === "owed-to-staff" ? "text-emerald-800" : copy.tone === "owed-from-staff" ? "text-rose-800" : "text-stone-700"}`}>
            {copy.tone === "settled" ? "Settled" : fmtMoney(Math.abs(summary.net))}
          </div>
          <div className="text-sm text-stone-700">{copy.text}</div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-sm">
          {Object.entries(TYPE_META).map(([k, m]) => (
            <div key={k} className="flex flex-col">
              <span className="mono text-[10px] uppercase tracking-wider text-stone-500">{m.short}</span>
              <span className="font-bold mono">{fmtMoney(summary.subtotals[k] || 0)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      {tab === "entries" && (<>
      <div className="flex flex-wrap gap-2 items-end bg-white rounded-[8px] border border-stone-200 p-3">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-stone-500">Type</Label>
          <Select value={filter.entry_type || "all"} onValueChange={(v) => setFilter({ ...filter, entry_type: v === "all" ? "" : v })}>
            <SelectTrigger className="w-48" data-testid="ledger-filter-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {Object.entries(TYPE_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-stone-500">From</Label>
          <Input type="date" value={filter.from} onChange={(e) => setFilter({ ...filter, from: e.target.value })} className="w-40" data-testid="ledger-filter-from" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-stone-500">To</Label>
          <Input type="date" value={filter.to} onChange={(e) => setFilter({ ...filter, to: e.target.value })} className="w-40" data-testid="ledger-filter-to" />
        </div>
        {(filter.entry_type || filter.from || filter.to) && (
          <Button variant="outline" onClick={() => setFilter({ entry_type: "", from: "", to: "" })}>Reset</Button>
        )}
      </div>

      {/* Entries table — Phase P.14 bank-statement style */}
      <StaffEntriesStatement entries={entries} TYPE_META={TYPE_META} onRemove={remove} onEdit={setEditing} />
      </>)}

      {/* Phase 28 — Statement view (chronological, with running balance) */}
      {tab === "statement" && <StatementView staffId={staffId} />}

      {creating && (
        <EntryDialog
          onClose={() => setCreating(null)}
          onSaved={() => { setCreating(null); load(); }}
          staffId={staffId}
          defaultEntryType={creating}
        />
      )}
      {/* Phase P.20 — ManagerPayoutDialog removed. */}
      {editing && <EntryDialog onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} staffId={staffId} initial={editing} />}
      {settling && (
        <SettleModal
          staffId={staffId}
          staffName={staffName}
          onClose={() => setSettling(false)}
          onSettled={() => { setSettling(false); load(); }}
        />
      )}
      <LedgerFullHistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        variant="staff"
        ownerId={staffId}
        ledgerLabel={staffName || "Staff"}
      />
    </div>
  );
}


// Phase P.20 — ManagerPayoutDialog deleted. Payout entries are legacy-only;
// new entries flow through the shared EntryDialog with the on/off-book
// (cash_type) radio.


function EntryDialog({ onClose, onSaved, staffId, initial, defaultEntryType }) {
  const [form, setForm] = useState({
    entry_type: initial?.entry_type || defaultEntryType || "expense",
    amount: initial?.amount || "",
    note: initial?.note || "",
    entry_date: (initial?.entry_date || new Date().toISOString()).slice(0, 10),
    booking_id: initial?.booking_id || "",
    // Phase P.20 — on-book (invoice) vs off-book (simple) toggle. Default
    // "invoice" for on-book because most manager-recorded ledger entries
    // are on-books unless the user explicitly says otherwise. Reuses the
    // canonical `cash_type` field.
    cash_type: initial?.cash_type || "invoice",
  });
  const [bookings, setBookings] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/bookings`, { params: { staff_id: staffId, limit: 50 } })
      .then(({ data }) => setBookings(Array.isArray(data) ? data.slice(0, 50) : []))
      .catch(() => {});
  }, [staffId]);

  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        entry_type: form.entry_type,
        amount: Number(form.amount),
        note: form.note,
        entry_date: form.entry_date ? `${form.entry_date}T12:00:00Z` : null,
        booking_id: form.booking_id || null,
        cash_type: form.cash_type || null,
      };
      if (initial) {
        await api.put(`/staff-ledger/${initial.id}`, body);
        toast.success("Entry updated");
      } else {
        await api.post(`/staff/${staffId}/ledger`, body);
        toast.success("Entry added");
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // Phase P.20 — when the dialog is opened via +/- CTAs (`defaultEntryType`)
  // hide the type dropdown and title from the flat expense/intake vocabulary
  // the user asked for. Editing legacy entries still shows the full picker.
  const preset = !initial && !!defaultEntryType;
  const isIntake = form.entry_type === "cash_intake";
  const titleText = preset
    ? (isIntake ? "Add intake" : "Add expense")
    : (initial ? "Edit ledger entry" : "Add ledger entry");

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ledger-entry-dialog">
        <DialogHeader>
          <DialogTitle>{titleText}</DialogTitle>
          <DialogDescription>
            {initial?.auto_generated
              ? "This entry was auto-created from a paid invoice. Edits here won't sync back to the invoice."
              : preset
                ? (isIntake
                    ? "Record cash you received. Positive amount, shows in Credit column."
                    : "Record cash you spent on company expenses. Negative amount, shows in Debit column.")
                : "All entry types are available to the manager."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {!preset && (
            <div>
              <Label>Type</Label>
              <Select value={form.entry_type} onValueChange={(v) => setForm({ ...form, entry_type: v })}>
                <SelectTrigger data-testid="ledger-entry-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Amount</Label>
            <Input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="ledger-entry-amount" />
          </div>
          <div>
            <Label>Date</Label>
            <Input type="date" value={form.entry_date} onChange={(e) => setForm({ ...form, entry_date: e.target.value })} data-testid="ledger-entry-date" />
          </div>
          <div>
            <Label>Booking (optional)</Label>
            <Select value={form.booking_id || "none"} onValueChange={(v) => setForm({ ...form, booking_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="ledger-entry-booking"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {bookings.map((b) => <SelectItem key={b.id} value={b.id}>{b.booking_code} · {(b.scheduled_start || "").slice(0, 10)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Note</Label>
            <Textarea rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="ledger-entry-note" />
          </div>
          {/* Phase P.20 — On-book / off-book radio (canonical `cash_type`).
              On-book → counts in accounts export + VAT. Off-book → excluded. */}
          <div>
            <Label>Book type</Label>
            <div className="grid grid-cols-2 gap-2 mt-1" role="radiogroup" aria-label="Book type">
              {[
                { code: "invoice", title: "On the book", sub: "In accounts · VAT" },
                { code: "simple",  title: "Off the book", sub: "Excluded from accounts" },
              ].map((opt) => {
                const on = form.cash_type === opt.code;
                return (
                  <button
                    key={opt.code}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    onClick={() => setForm({ ...form, cash_type: opt.code })}
                    className={`text-left rounded-[8px] border px-3 py-2 transition-colors ${
                      on
                        ? "border-accent bg-accent/10 ring-2 ring-accent/30"
                        : "border-border hover:border-accent/60"
                    }`}
                    data-testid={`ledger-entry-cashtype-${opt.code === "invoice" ? "on" : "off"}`}
                  >
                    <div className="text-sm font-bold">{opt.title}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">{opt.sub}</div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={busy} onClick={submit} data-testid="ledger-entry-save" className="bg-stone-900 hover:bg-stone-800 text-white">
            {busy ? "Saving…" : initial ? "Save" : "Add entry"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Phase 28 — Settle-to-Zero confirmation modal.
const SETTLE_METHODS = [
  { code: "cash_received",       label: "Cash received from staff" },
  { code: "bank_transfer",       label: "Bank transfer" },
  { code: "adjustment_writeoff", label: "Adjustment / write-off" },
  { code: "other",               label: "Other" },
];

function SettleModal({ staffId, staffName, onClose, onSettled }) {
  const [outstanding, setOutstanding] = useState(null);
  const [method, setMethod] = useState("cash_received");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // Phase 30 — capture the freshly-created settlement so we can offer the
  // "Print receipt" CTA on a brief post-settle success view.
  const [completed, setCompleted] = useState(null);

  const fetchOutstanding = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get(`/staff/${staffId}/ledger/summary`);
      setOutstanding(Number(data.outstanding_balance || 0));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setRefreshing(false); }
  };
  useEffect(() => { fetchOutstanding(); /* eslint-disable-next-line */ }, [staffId]);

  const submit = async () => {
    if (outstanding == null) return;
    setBusy(true);
    try {
      // Defensive re-fetch: confirm the balance is still the same as displayed
      // before submitting, so we can show a helpful error rather than letting
      // the backend race-guard reject us silently.
      const { data: fresh } = await api.get(`/staff/${staffId}/ledger/summary`);
      const freshOutstanding = Number(fresh.outstanding_balance || 0);
      if (Math.abs(freshOutstanding - outstanding) > 0.005) {
        setOutstanding(freshOutstanding);
        toast.error("Balance changed — please review the new amount and confirm again.");
        setBusy(false);
        return;
      }
      const { data } = await api.post(`/staff/${staffId}/settle`, {
        method,
        amount: Math.abs(outstanding),
        notes,
      });
      toast.success(`Settled ${fmtMoney(Math.abs(outstanding))} to zero. Statement updated.`);
      setCompleted(data?.settlement || null);
      // Notify the parent so it refreshes the ledger in the background, but
      // keep the modal open so the operator can print the receipt.
      onSettled?.(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const direction = outstanding == null ? "" : outstanding > 0
    ? "Company will pay this to staff"
    : "Staff will return this cash to company";
  const directionTone = outstanding == null ? "text-stone-500"
    : outstanding > 0 ? "text-emerald-700" : "text-rose-700";

  // ----- Post-settle success view -----
  if (completed) {
    return (
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent data-testid="settle-success-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="h-5 w-5" /> Settlement recorded
            </DialogTitle>
            <DialogDescription>
              {staffName}&apos;s ledger is now at €0.00. A receipt is ready to download.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border border-emerald-300 bg-emerald-50/70 p-4 space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-stone-600">Amount</span>
              <span className="font-bold tabular-nums">{fmtMoney(Math.abs(completed.amount_settled || 0))}</span></div>
            <div className="flex justify-between"><span className="text-stone-600">Method</span>
              <span className="font-bold">{(SETTLE_METHODS.find((m) => m.code === completed.method) || {}).label || completed.method}</span></div>
            <div className="flex justify-between"><span className="text-stone-600">Receipt #</span>
              <span className="mono">{(completed.id || "").split("-")[0].toUpperCase()}</span></div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => downloadSettlementReceipt(completed.id)}
              className="gap-2"
              data-testid="settle-success-print-receipt"
            >
              <Printer className="h-4 w-4" /> Print receipt
            </Button>
            <Button onClick={onClose} className="bg-emerald-700 hover:bg-emerald-600 text-white" data-testid="settle-success-done">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="settle-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-700" />
            Settle {staffName}&apos;s ledger to zero
          </DialogTitle>
          <DialogDescription>
            Records a single immutable settlement event covering every currently-outstanding ledger entry. The balance resets to 0; historical entries are preserved in the Statement tab.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="rounded-md border-2 border-emerald-300 bg-emerald-50/60 p-4">
            <div className="mono text-[10px] uppercase tracking-wider text-stone-600">Outstanding balance</div>
            <div
              className="text-4xl font-black text-stone-900 mt-1 tabular-nums"
              data-testid="settle-outstanding-amount"
            >
              {outstanding == null
                ? <Loader2 className="h-7 w-7 animate-spin text-stone-400" />
                : fmtMoney(Math.abs(outstanding))}
            </div>
            <div className={`text-xs mt-1 ${directionTone}`}>{direction}</div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-wider text-stone-500">Settlement method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="settle-method-picker"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SETTLE_METHODS.map((m) => (
                  <SelectItem key={m.code} value={m.code}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-wider text-stone-500">Notes <span className="opacity-60 normal-case">(optional)</span></Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="e.g. Cash counted in office on Friday."
              data-testid="settle-notes"
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={fetchOutstanding} disabled={refreshing || busy} data-testid="settle-refresh">
            {refreshing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
            Refresh balance
          </Button>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="settle-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || outstanding == null || Math.abs(outstanding) < 0.005}
            className="bg-emerald-700 hover:bg-emerald-600 text-white"
            data-testid="settle-confirm"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
            Confirm Settlement
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Phase 28 — Bank-statement-style chronological view.
function StatementView({ staffId }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState({ from: "", to: "" });

  const load = async () => {
    try {
      const params = {};
      if (filter.from) params.from = filter.from;
      if (filter.to) params.to = filter.to;
      const { data } = await api.get(`/staff/${staffId}/statement`, { params });
      setData(data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [staffId, filter]);

  return (
    <div className="space-y-3" data-testid="ledger-statement-view">
      <div className="flex flex-wrap items-end gap-2 bg-white rounded-[8px] border border-stone-200 p-3">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-stone-500">From</Label>
          <Input type="date" value={filter.from} onChange={(e) => setFilter({ ...filter, from: e.target.value })} className="w-40" data-testid="statement-filter-from" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-stone-500">To</Label>
          <Input type="date" value={filter.to} onChange={(e) => setFilter({ ...filter, to: e.target.value })} className="w-40" data-testid="statement-filter-to" />
        </div>
        {(filter.from || filter.to) && (
          <Button variant="outline" onClick={() => setFilter({ from: "", to: "" })}>Reset</Button>
        )}
        <div className="ml-auto text-right">
          <div className="mono text-[10px] uppercase tracking-wider text-stone-500">Closing balance</div>
          <div className={`text-2xl font-black tabular-nums ${(data?.closing_balance ?? 0) > 0.005 ? "text-emerald-700" : (data?.closing_balance ?? 0) < -0.005 ? "text-rose-700" : "text-stone-600"}`}
               data-testid="statement-closing-balance">
            {fmtMoney(Math.abs(data?.closing_balance ?? 0))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-[8px] border border-stone-200 overflow-x-auto">
        <table className="w-full text-sm" data-testid="statement-table">
          <thead className="bg-stone-50 text-stone-600">
            <tr>
              <th className="text-left px-3 py-2 w-28">Date</th>
              <th className="text-left px-3 py-2">Event</th>
              <th className="text-right px-3 py-2 w-32">Amount</th>
              <th className="text-right px-3 py-2 w-36">Running balance</th>
            </tr>
          </thead>
          <tbody>
            {(data?.rows || []).map((r) => {
              if (r.kind === "settlement") {
                const methodLabel = (SETTLE_METHODS.find((m) => m.code === r.method) || {}).label || r.method;
                // Phase P.29 — visual thick divider ABOVE the settlement
                // row so the checkpoint is impossible to miss when
                // scanning a long statement.
                return (
                  <React.Fragment key={r.id}>
                    <tr data-testid={`statement-row-settle-divider-${r.id}`}>
                      <td colSpan={4} className="p-0">
                        <div className="flex items-center gap-3 py-2 px-2">
                          <div className="h-[2px] flex-1 bg-[#03313A]" />
                          <span className="mono text-[10px] uppercase tracking-widest text-[#03313A] font-bold whitespace-nowrap">
                            Settled {(r.date || "").slice(0, 10)}
                          </span>
                          <div className="h-[2px] flex-1 bg-[#03313A]" />
                        </div>
                      </td>
                    </tr>
                    <tr className="border-t border-sky-200 bg-sky-50/70" data-testid={`statement-row-settlement-${r.id}`}>
                    <td className="px-3 py-2 mono text-xs text-sky-800">{(r.date || "").slice(0, 10)}</td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1.5 text-sky-800 font-bold">
                        <CheckCircle2 className="h-4 w-4" /> Settled · {methodLabel}
                      </span>
                      {r.notes && <div className="text-[11px] text-sky-700 mt-0.5">{r.notes}</div>}
                      <div className="text-[10px] mono text-sky-700/80 mt-0.5">
                        Balance before {fmtMoney(Math.abs(r.balance_before || 0))} → 0
                      </div>
                      <button
                        type="button"
                        onClick={() => downloadSettlementReceipt(r.id)}
                        className="mt-1 inline-flex items-center gap-1 text-[11px] text-sky-700 hover:text-sky-900 underline-offset-2 hover:underline"
                        data-testid={`statement-row-settlement-print-${r.id}`}
                      >
                        <Printer className="h-3 w-3" /> Print receipt
                      </button>
                    </td>
                    <td className="px-3 py-2 text-right mono font-bold text-sky-800">
                      {fmtMoney(Math.abs(r.amount || 0))}
                    </td>
                    <td className="px-3 py-2 text-right mono font-bold text-sky-800 tabular-nums">
                      {fmtMoney(r.running_balance || 0)}
                    </td>
                  </tr>
                  </React.Fragment>
                );
              }
              const m = TYPE_META[r.entry_type] || TYPE_META.expense;
              const Icon = m.icon;
              const signed = Number(r.signed_amount || 0);
              return (
                <tr key={r.id} className="border-t border-stone-200" data-testid={`statement-row-entry-${r.id}`}>
                  <td className="px-3 py-2 mono text-xs text-stone-600">{(r.date || "").slice(0, 10)}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center gap-1.5 ${m.color}`}>
                      <Icon className="h-3.5 w-3.5" /> {m.label}
                    </span>
                    {r.note && <div className="text-[11px] text-stone-600 mt-0.5">{r.note}</div>}
                    {r.booking_code && (
                      <div className="text-[10px] mono text-stone-500 inline-flex items-center gap-1"><LinkIcon className="h-3 w-3" /> {r.booking_code}</div>
                    )}
                    {r.settlement_id && (
                      <div className="text-[10px] mono uppercase text-sky-700 tracking-wider mt-0.5">Settled</div>
                    )}
                  </td>
                  <td className={`px-3 py-2 text-right mono font-bold ${signed > 0 ? "text-emerald-700" : signed < 0 ? "text-rose-700" : "text-stone-700"}`}>
                    {signed > 0 ? "+" : ""}{fmtMoney(Math.abs(signed), r.currency || "EUR")}
                  </td>
                  <td className="px-3 py-2 text-right mono tabular-nums">
                    {fmtMoney(r.running_balance || 0)}
                  </td>
                </tr>
              );
            })}
            {(data?.rows || []).length === 0 && (
              <tr><td colSpan={4} className="px-3 py-8 text-center text-stone-400">No events yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {data && (
        <div className="text-[11px] mono text-stone-500 px-1">
          {data.entry_count} ledger entr{data.entry_count === 1 ? "y" : "ies"} · {data.settlement_count} settlement{data.settlement_count === 1 ? "" : "s"}
        </div>
      )}
    </div>
  );
}

// Phase 60.1 — reconcile dialog. Shows the dry-run orphan list with a
// per-booking row, then confirms before backfilling.
const RECONCILE_PAGE_SIZE = 50;
const RECONCILE_REASON_LABEL = {
  approval_pre_phase58: "Pre-Phase-58 approval",
  booking_paid_no_approval: "Booking paid · no approval",
  invoice_paid_no_entry: "Invoice paid · no entry",
};

function ReconcileDialog({ open, onOpenChange, report, busy, page, onPageChange, onExecute }) {
  const orphans = report?.orphans || [];
  const total = orphans.length;
  const totalPages = Math.max(1, Math.ceil(total / RECONCILE_PAGE_SIZE));
  const slice = orphans.slice(page * RECONCILE_PAGE_SIZE, (page + 1) * RECONCILE_PAGE_SIZE);
  const sumAmount = orphans.reduce((acc, o) => acc + (Number(o.amount) || 0), 0);
  const byReason = report?.by_reason || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[92vw]" data-testid="reconcile-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5" /> Reconcile staff ledger
          </DialogTitle>
          <DialogDescription>
            Surfaces cash events (approvals, paid bookings, paid cash invoices)
            that should have produced a staff ledger entry but didn&apos;t.
            Inspect the list, then click <span className="font-bold">Execute backfill</span>{" "}
            to write the missing entries. Idempotent — running again is a no-op.
          </DialogDescription>
        </DialogHeader>

        {busy && !report && (
          <div className="flex items-center justify-center py-12 text-stone-500" data-testid="reconcile-loading">
            <Loader2 className="h-6 w-6 animate-spin mr-2" /> Scanning…
          </div>
        )}

        {report && total === 0 && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-md p-6 text-center"
               data-testid="reconcile-empty">
            <CheckCircle2 className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
            <div className="font-bold text-emerald-900">All clean — no orphans found.</div>
            <div className="text-xs text-emerald-800/80 mt-1">
              Every approved cash event has a matching staff ledger entry.
            </div>
          </div>
        )}

        {report && total > 0 && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <div className="bg-amber-50 border border-amber-200 rounded p-2"
                   data-testid="reconcile-total-count">
                <div className="mono uppercase tracking-wider text-stone-500">Orphans</div>
                <div className="text-lg font-black text-amber-900">{total}</div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded p-2">
                <div className="mono uppercase tracking-wider text-stone-500">Sum amount</div>
                <div className="text-lg font-black text-amber-900">{fmtMoney(sumAmount)}</div>
              </div>
              {Object.entries(byReason).filter(([, n]) => n > 0).map(([r, n]) => (
                <div key={r} className="bg-stone-50 border border-stone-200 rounded p-2">
                  <div className="mono uppercase tracking-wider text-stone-500 truncate">{RECONCILE_REASON_LABEL[r] || r}</div>
                  <div className="text-lg font-black">{n}</div>
                </div>
              ))}
            </div>

            <div className="border border-stone-200 rounded-md overflow-hidden">
              <table className="w-full text-xs" data-testid="reconcile-table">
                <thead className="bg-stone-100 text-stone-700">
                  <tr>
                    <th className="text-left px-2 py-1.5 font-bold">Booking</th>
                    <th className="text-left px-2 py-1.5 font-bold">Staff</th>
                    <th className="text-left px-2 py-1.5 font-bold">Customer</th>
                    <th className="text-right px-2 py-1.5 font-bold">Amount</th>
                    <th className="text-left px-2 py-1.5 font-bold">Cash</th>
                    <th className="text-left px-2 py-1.5 font-bold">Date</th>
                    <th className="text-left px-2 py-1.5 font-bold">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {slice.map((o) => (
                    <tr key={o.booking_id} className="border-t border-stone-200 hover:bg-stone-50"
                        data-testid={`reconcile-row-${o.booking_id}`}>
                      <td className="px-2 py-1.5 mono">{o.booking_code || "—"}</td>
                      <td className="px-2 py-1.5 truncate max-w-[120px]">{o.staff_name || "—"}</td>
                      <td className="px-2 py-1.5 truncate max-w-[160px]">{o.customer_name || "—"}</td>
                      <td className="px-2 py-1.5 mono tabular-nums text-right">{fmtMoney(o.amount)}</td>
                      <td className="px-2 py-1.5">
                        <span className={`mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                          o.cash_type === "simple"
                            ? "bg-violet-50 text-violet-700 border-violet-200"
                            : "bg-sky-50 text-sky-700 border-sky-200"
                        }`}>{o.cash_type}</span>
                      </td>
                      <td className="px-2 py-1.5 text-stone-600 whitespace-nowrap">
                        {(o.decided_at || "").slice(0, 10) || "—"}
                      </td>
                      <td className="px-2 py-1.5">
                        <span className="inline-flex items-center gap-1 mono text-[10px] uppercase tracking-wider text-amber-700">
                          <AlertTriangle className="h-3 w-3" />
                          {RECONCILE_REASON_LABEL[o.reason] || o.reason}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between text-xs text-stone-600">
                <div>
                  Page {page + 1} / {totalPages} · showing {slice.length} of {total}
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="outline" disabled={page <= 0}
                          onClick={() => onPageChange(Math.max(0, page - 1))}
                          data-testid="reconcile-page-prev">
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="outline" disabled={page >= totalPages - 1}
                          onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
                          data-testid="reconcile-page-next">
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}
                  data-testid="reconcile-cancel">
            Close
          </Button>
          {report && total > 0 && (
            <Button onClick={onExecute} disabled={busy}
                    className="bg-amber-600 hover:bg-amber-700 text-white gap-2"
                    data-testid="reconcile-execute">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wrench className="h-4 w-4" />}
              Execute backfill ({total})
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Phase P.14 — bank-statement-style entries table for the Staff Ledger drill-down.
// Columns: Date | Description | Credit | Debit | Balance. Running balance is
// computed client-side by walking entries chronologically. Credit/Debit sign
// is derived from TYPE_META[entry_type].sign (matches the existing statement
// tab semantics).
function StaffEntriesStatement({ entries, TYPE_META, onRemove, onEdit }) {
  const asc = useMemo(
    () => [...entries].sort((a, b) => String(a.entry_date || "").localeCompare(String(b.entry_date || ""))),
    [entries],
  );
  const balanceById = useMemo(() => {
    const map = new Map();
    let running = 0;
    for (const e of asc) {
      const m = TYPE_META[e.entry_type] || TYPE_META.expense;
      running += m.sign * Number(e.amount || 0);
      map.set(e.id, running);
    }
    return map;
  }, [asc, TYPE_META]);
  const totals = useMemo(() => {
    let credits = 0; let debits = 0;
    for (const e of entries) {
      const m = TYPE_META[e.entry_type] || TYPE_META.expense;
      const signed = m.sign * Number(e.amount || 0);
      if (signed >= 0) credits += signed; else debits += Math.abs(signed);
    }
    return { credits, debits, balance: credits - debits };
  }, [entries, TYPE_META]);
  return (
    <div>
      <div className="bg-stone-100 border border-stone-200 rounded-t-[8px] px-4 py-2 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs mono"
           data-testid="ledger-statement-totals">
        <div><span className="uppercase tracking-widest text-stone-500">Credits</span> <span className="font-black text-emerald-700">+{fmtMoney(totals.credits)}</span></div>
        <div><span className="uppercase tracking-widest text-stone-500">Debits</span> <span className="font-black text-rose-700">−{fmtMoney(totals.debits)}</span></div>
        <div><span className="uppercase tracking-widest text-stone-500">Balance</span> <span className={`font-black tabular-nums ${totals.balance < 0 ? "text-rose-800" : ""}`}>{fmtMoney(totals.balance)}</span></div>
      </div>
      <div className="bg-white rounded-b-[8px] border border-stone-200 border-t-0 overflow-x-auto">
        <table className="w-full text-sm" data-testid="ledger-entries-table">
          <thead className="bg-stone-50 text-stone-600">
            <tr>
              <th className="text-left px-3 py-2 font-bold mono text-[10px] uppercase tracking-widest">Date</th>
              <th className="text-left px-3 py-2 font-bold mono text-[10px] uppercase tracking-widest">Description</th>
              <th className="text-right px-3 py-2 font-bold mono text-[10px] uppercase tracking-widest w-28">Credit</th>
              <th className="text-right px-3 py-2 font-bold mono text-[10px] uppercase tracking-widest w-28">Debit</th>
              <th className="text-right px-3 py-2 font-bold mono text-[10px] uppercase tracking-widest w-32">Balance</th>
              <th className="text-right px-3 py-2 w-24"> </th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => {
              const m = TYPE_META[e.entry_type] || TYPE_META.expense;
              const signed = m.sign * Number(e.amount || 0);
              const amt = Math.abs(Number(e.amount || 0));
              const balance = balanceById.get(e.id) ?? 0;
              return (
                <tr key={e.id} className="border-t border-stone-200 hover:bg-stone-50" data-testid={`ledger-entry-${e.id}`}>
                  <td className="px-3 py-2 mono text-xs text-stone-600 whitespace-nowrap align-top">{(e.entry_date || "").slice(0, 10)}</td>
                  <td className="px-3 py-2 align-top">
                    <div className="text-stone-800 font-bold">{m.label}</div>
                    {e.note && <div className="text-xs text-stone-600 mt-0.5">{e.note}</div>}
                    <div className="flex flex-wrap items-center gap-1.5 mt-1">
                      {e.booking_code && (
                        <span className="text-[11px] mono text-stone-500 inline-flex items-center gap-1"><LinkIcon className="h-3 w-3" />{e.booking_code}</span>
                      )}
                      {e.linked_invoice_number && (
                        <span className="text-[11px] mono text-amber-700">→ {e.linked_invoice_number}</span>
                      )}
                      {e.photo_path && (
                        <a href={e.photo_path} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-stone-500 hover:text-stone-800 text-[11px]">
                          <ImageIcon className="h-3 w-3" /> photo
                        </a>
                      )}
                      {e.auto_generated && (
                        <span className="text-[10px] mono uppercase bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 rounded" data-testid={`ledger-entry-auto-${e.id}`}>Auto</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right align-top mono tabular-nums font-bold whitespace-nowrap">
                    {signed > 0 ? <span className="text-emerald-700">+{fmtMoney(amt, e.currency || "EUR")}</span> : <span className="text-stone-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right align-top mono tabular-nums font-bold whitespace-nowrap">
                    {signed < 0 ? <span className="text-rose-700">−{fmtMoney(amt, e.currency || "EUR")}</span> : <span className="text-stone-300">—</span>}
                  </td>
                  <td className={`px-3 py-2 text-right align-top mono tabular-nums font-black whitespace-nowrap ${balance < 0 ? "text-rose-800" : "text-stone-800"}`}>
                    {fmtMoney(balance, e.currency || "EUR")}
                  </td>
                  <td className="px-3 py-2 text-right align-top">
                    <div className="inline-flex items-center gap-1">
                      <Button variant="outline" size="sm" onClick={() => onEdit(e)} data-testid={`ledger-entry-edit-${e.id}`}><Pencil className="h-3 w-3" /></Button>
                      <Button variant="outline" size="sm" onClick={() => onRemove(e)} className="text-rose-700 hover:text-rose-900" data-testid={`ledger-entry-delete-${e.id}`}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {entries.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-stone-400">No entries match.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// Phase P.37 — expose the drilldown component for the unified Ledger page.
export const StaffLedgerDetail = StaffDrillDown;

